/**
 * Pure, zero-React utilities for mosaic layout local persistence.
 * No hooks, no side effects — safe to unit-test directly.
 */

import { getLeaves, type MosaicNode } from 'react-mosaic-component';
import { ALL_TILE_IDS, DEFAULT_MOSAIC_TREE, MOSAIC_STORAGE_KEY, type TileId } from './types';

const VALID_TILE_IDS = new Set<string>(ALL_TILE_IDS);

export function isValidTree(tree: MosaicNode<TileId>): boolean {
  const leaves = getLeaves(tree);
  const unique = new Set(leaves);
  if (leaves.length !== unique.size) return false;
  return leaves.every((id) => VALID_TILE_IDS.has(id));
}

export function loadTreeFromStorage(): MosaicNode<TileId> {
  try {
    const stored = localStorage.getItem(MOSAIC_STORAGE_KEY);
    if (stored) {
      const tree = JSON.parse(stored) as MosaicNode<TileId>;
      if (isValidTree(tree)) return tree;
      localStorage.removeItem(MOSAIC_STORAGE_KEY);
    }
  } catch {
    localStorage.removeItem(MOSAIC_STORAGE_KEY);
  }
  return DEFAULT_MOSAIC_TREE;
}

export function persistTreeLocally(tree: MosaicNode<TileId> | null): void {
  if (tree) {
    localStorage.setItem(MOSAIC_STORAGE_KEY, JSON.stringify(tree));
  } else {
    localStorage.removeItem(MOSAIC_STORAGE_KEY);
  }
}

/** Type guard: parses an unknown API value as MosaicNode<TileId> or returns null. */
export function parseMosaicLayout(value: unknown): MosaicNode<TileId> | null {
  if (!value) return null;
  try {
    const tree = value as MosaicNode<TileId>;
    return isValidTree(tree) ? tree : null;
  } catch {
    return null;
  }
}
